import React, { useMemo, useState, useEffect } from 'react';
import { ProcessingPipeline } from './ProcessingPipeline';
import { CandidateRankingRow } from './CandidateRankingRow';
import { useAuth } from '../../context/AuthContext';

const pill = (active) => ({ fontSize: '0.72rem', fontWeight: 700, padding: '0.25rem 0.7rem', borderRadius: 999, border: 'none', cursor: 'pointer', background: active ? '#fff' : 'transparent', color: active ? '#0076A3' : '#78716C', boxShadow: active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none', transition: 'background-color 0.15s, color 0.15s' });

// Ranked candidate list + inline processing pipeline for the Job Detail page.
export function CandidateList({ candidates, navigate, onSendAssessment, onVerify, onParseLead, jobId }) {
  const { user } = useAuth();
  const prefKey = `riq_blend_${user?.id || 'anon'}_${jobId || 'x'}`;
  const [pref] = useState(() => { try { return JSON.parse(localStorage.getItem(prefKey)) || {}; } catch { return {}; } });
  const [blend, setBlend] = useState(!!pref.blend);
  const [weight, setWeight] = useState(typeof pref.weight === 'number' ? pref.weight : 0.3);
  useEffect(() => { try { localStorage.setItem(prefKey, JSON.stringify({ blend, weight })); } catch { /* ignore */ } }, [blend, weight, prefKey]);
  const completed = useMemo(() => candidates.filter(c => c.status === 'completed'), [candidates]);
  const processing = useMemo(() => candidates.filter(c => ['uploaded', 'parsing', 'matching'].includes(c.status)), [candidates]);
  const leads = useMemo(() => candidates.filter(c => c.status === 'lead'), [candidates]);
  const hasFits = useMemo(() => completed.some(c => c.behavioral_fit), [completed]);
  const sortedCompleted = useMemo(() => {
    const arr = completed.map((c) => {
      const fit = c.behavioral_fit?.score;
      const blended = fit != null ? Math.round((1 - weight) * (c.overall_score || 0) + weight * fit) : (c.overall_score || 0);
      return { ...c, _blended: blended, _hasFit: fit != null };
    });
    arr.sort((a, b) => (blend ? (b._blended - a._blended) : ((b.overall_score || 0) - (a.overall_score || 0))));
    return arr;
  }, [completed, blend, weight]);

  return (
    <div>
      <ProcessingPipeline candidates={processing} />

      {leads.length > 0 && (
        <div data-testid="leads-section" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '0.9375rem', fontWeight: 600 }}>New Applicants</h3>
            <span style={{ fontSize: '0.6875rem', fontWeight: 700, color: '#0F766E', background: '#CCFBF1', border: '1px solid #99F6E4', borderRadius: 999, padding: '0.125rem 0.5rem' }}>{leads.length} lead{leads.length !== 1 ? 's' : ''}</span>
            <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>from your public job page — parse to AI-score</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {leads.map((l) => (
              <div key={l._id} data-testid="lead-row" onClick={() => navigate(`/candidates/${l._id}`)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.85rem 1.1rem', borderRadius: 12, background: '#F0FDFA', border: '1px solid #99F6E4', cursor: 'pointer' }} className="candidate-row">
                <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,#0D9488,#00A0E0)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 600, fontSize: '0.8125rem', flexShrink: 0 }}>
                  {l.name?.charAt(0)?.toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#002A38' }}>{l.name}</span>
                    <span style={{ fontSize: '0.5625rem', fontWeight: 700, color: '#0F766E', background: '#CCFBF1', borderRadius: 999, padding: '0.125rem 0.375rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Lead</span>
                    {l.source && (
                      <span data-testid="lead-source-badge" style={{ fontSize: '0.5625rem', fontWeight: 700, color: '#475569', background: '#EEF2F7', border: '1px solid #ECE7DE', borderRadius: 999, padding: '0.125rem 0.4rem' }}>via {l.source}</span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {l.lead_email || l.parsed_resume?.email || '—'}{(l.lead_phone || l.parsed_resume?.phone) ? ` · ${l.lead_phone || l.parsed_resume.phone}` : ''}
                  </div>
                </div>
                <button
                  data-testid="parse-lead-button"
                  onClick={(e) => { e.stopPropagation(); onParseLead(l); }}
                  style={{ padding: '0.4rem 0.85rem', borderRadius: 999, border: 'none', background: '#0D9488', color: '#fff', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap' }}
                >
                  Parse &amp; score
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '0.9375rem', fontWeight: 600 }}>
          Ranked Candidates
          <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', fontWeight: 400 }}>
            ({completed.length})
          </span>
        </h3>
        {hasFits && (
          <div data-testid="fit-blend-control" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.7rem', color: '#A8A29E', fontWeight: 600 }}>Rank by</span>
            <div style={{ display: 'inline-flex', background: '#EDF4F7', borderRadius: 999, padding: 2 }}>
              <button data-testid="rank-mode-skills" aria-pressed={!blend} onClick={() => setBlend(false)} style={pill(!blend)}>Skills</button>
              <button data-testid="rank-mode-blended" aria-pressed={blend} onClick={() => setBlend(true)} style={pill(blend)}>Skills + Culture</button>
            </div>
            {blend && (
              <label title="Culture fit is capped at 60% so skills always carry at least 40% of the ranking" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', color: '#78716C' }}>
                Culture weight
                <input type="range" min="0" max="60" step="5" value={Math.round(weight * 100)} data-testid="fit-blend-weight"
                  aria-label="Culture weight percent" aria-valuenow={Math.round(weight * 100)}
                  onChange={(e) => setWeight(Number(e.target.value) / 100)} style={{ accentColor: '#0076A3', width: 90 }} />
                <span style={{ fontWeight: 700, color: '#002A38', width: 32 }}>{Math.round(weight * 100)}%</span>
              </label>
            )}
          </div>
        )}
      </div>

      {completed.length === 0 && processing.length === 0 ? (
        <div className="empty-state" style={{ paddingTop: '3rem' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 48, height: 48 }}>
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
          </svg>
          <p style={{ marginTop: '1rem' }}>No candidates yet. Upload resumes to get started.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {sortedCompleted.map((c, idx) => (
            <CandidateRankingRow
              key={c._id}
              candidate={c}
              rank={idx}
              blendMode={blend}
              blendedScore={c._blended}
              hasFit={c._hasFit}
              onOpen={(cand) => navigate(`/candidates/${cand._id}`)}
              onSendAssessment={onSendAssessment}
              onVerify={onVerify}
            />
          ))}
        </div>
      )}
    </div>
  );
}
